import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import {
  Loader2, Inbox, Send, FileText, Receipt, CalendarClock, Truck,
  CreditCard, Store, Briefcase, ArrowRight, CheckCircle2, AlertCircle, Wallet,
} from "lucide-react";
import { StatusBadge, money, fmtDate } from "../pages/pro/proUi";

// Dashboard Professional — véritable hub métier bâti sur les endpoints existants
// (+ /marketplace/quotes/mine additif). Aucune donnée inventée, isolation backend inchangée.
export default function ProfessionalDashboard() {
  const { identity } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      const safe = (p) => p.then((r) => r.data).catch(() => null);
      const [profile, slots, orders, connect, proposals, quotes, requests] = await Promise.all([
        safe(api.get("/marketplace/profile/me")),
        safe(api.get("/marketplace/availability/me")),
        safe(api.get("/marketplace/orders")),
        safe(api.get("/marketplace/connect/status")),
        safe(api.get("/marketplace/my-proposals")),
        safe(api.get("/marketplace/quotes/mine")),
        safe(api.get("/marketplace/requests")),
      ]);
      if (alive) setS({ loading: false, profile, slots, orders, connect, proposals, quotes, requests });
    })();
    return () => { alive = false; };
  }, []);

  if (s.loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const profile = s.profile?.profile || s.profile || {};
  const now = new Date().toISOString();
  const slots = s.slots?.slots || [];
  const orders = (s.orders?.orders || []).filter((o) => o.role === "pro");
  const proposals = s.proposals?.proposals || [];
  const quotes = (s.quotes?.quotes || []).filter((q) => q.is_pro);
  const openRequests = s.requests?.requests || [];
  const connect = s.connect || {};
  const connectReady = !!(connect.charges_enabled && connect.payouts_enabled);

  const toDeliver = orders.filter((o) => o.status === "PAID");
  const activeOrders = orders.filter((o) => ["PAID", "DELIVERED"].includes(o.status));
  const upcomingBookings = slots.filter((x) => x.status === "BOOKED" && x.start >= now);
  const revenue = orders.filter((o) => o.status === "COMPLETED").reduce((a, o) => a + (o.pro_amount || 0), 0);

  const kpis = [
    { key: "demandes", label: "Demandes ouvertes", value: openRequests.length, icon: Inbox, to: "/pro/demandes" },
    { key: "propositions", label: "Propositions envoyées", value: proposals.length, icon: Send, to: "/pro/propositions" },
    { key: "devis", label: "Devis en attente", value: quotes.filter((q) => q.status === "SENT").length, icon: FileText, to: "/pro/devis" },
    { key: "commandes", label: "Commandes actives", value: activeOrders.length, icon: Receipt, to: "/pro/commandes" },
  ];

  const quick = [
    { label: "Demandes", icon: Inbox, to: "/pro/demandes" },
    { label: "Propositions", icon: Send, to: "/pro/propositions" },
    { label: "Devis", icon: FileText, to: "/pro/devis" },
    { label: "Commandes", icon: Receipt, to: "/pro/commandes" },
    { label: "Réservations", icon: CalendarClock, to: "/pro/reservations" },
    { label: "Factures", icon: FileText, to: "/pro/factures" },
    { label: "Profil & services", icon: Briefcase, to: "/pro" },
    { label: "Marché", icon: Store, to: "/marketplace" },
  ];

  return (
    <div className="space-y-6" data-testid="professional-dashboard">
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">{identity?.space_label || "Espace professionnel"}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1" data-testid="pro-dashboard-greeting">
          {profile.headline || "Mon activité pro"} 💼
        </h1>
        <p className="text-xs text-zinc-500 mt-1">Pilote ton activité : demandes, propositions, devis, commandes, réservations et paiements.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pro-stats">
        {kpis.map((k) => (
          <button key={k.key} onClick={() => navigate(k.to)} data-testid={`pro-stat-${k.key}`}
            className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover">
            <k.icon className="w-5 h-5 text-amber-400 mb-2" />
            <div className="font-display text-2xl font-extrabold">{k.value}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{k.label}</div>
          </button>
        ))}
      </div>

      {/* Bandeau actions prioritaires */}
      {(toDeliver.length > 0 || upcomingBookings.length > 0) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {toDeliver.length > 0 && (
            <button onClick={() => navigate("/pro/commandes")} data-testid="pro-todeliver" className="text-left bg-violet-500/10 border border-violet-500/30 rounded-2xl p-4 flex items-center gap-3">
              <Truck className="w-6 h-6 text-violet-300" />
              <div><div className="font-semibold text-zinc-100">{toDeliver.length} prestation(s) à livrer</div><div className="text-xs text-zinc-400">Commandes payées en attente de livraison</div></div>
              <ArrowRight className="w-4 h-4 text-violet-300 ml-auto" />
            </button>
          )}
          {upcomingBookings.length > 0 && (
            <button onClick={() => navigate("/pro/reservations")} data-testid="pro-bookings" className="text-left bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex items-center gap-3">
              <CalendarClock className="w-6 h-6 text-amber-300" />
              <div><div className="font-semibold text-zinc-100">{upcomingBookings.length} réservation(s) à venir</div><div className="text-xs text-zinc-400">Prochaine : {fmtDate(upcomingBookings[0].start)}</div></div>
              <ArrowRight className="w-4 h-4 text-amber-300 ml-auto" />
            </button>
          )}
        </div>
      )}

      {/* Paiements + Revenu */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-payments-card">
          <div className="flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5 text-amber-400" /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Paiements & versements</span></div>
          <div className={`flex items-center gap-2 text-sm ${connectReady ? "text-emerald-400" : "text-amber-300"}`}>
            {connectReady ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {connectReady ? "Compte de versement actif" : "Configure ton compte pour recevoir tes paiements"}
          </div>
          <button onClick={() => navigate("/pro")} data-testid="pro-connect-cta" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:gap-2.5 transition-all">Gérer mes paiements <ArrowRight className="w-4 h-4" /></button>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-revenue-card">
          <div className="flex items-center gap-2 mb-3"><Wallet className="w-5 h-5 text-amber-400" /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Revenus encaissés</span></div>
          <div className="font-display text-3xl font-extrabold text-amber-400">{money(revenue)}</div>
          <p className="text-xs text-zinc-500 mt-1">Montant net reçu sur tes prestations finalisées.</p>
        </div>
      </div>

      {/* Accès rapides */}
      <div>
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Accès rapides</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2" data-testid="pro-quick">
          {quick.map((q) => (
            <button key={q.to + q.label} onClick={() => navigate(q.to)} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 flex flex-col items-start gap-2 card-hover">
              <q.icon className="w-5 h-5 text-amber-400" /><span className="text-sm text-zinc-200">{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Activité récente */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="pro-activity-card">
        <div className="flex items-center gap-2 mb-3"><Receipt className="w-5 h-5 text-amber-400" /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Commandes récentes</span></div>
        {orders.length === 0 ? (
          <p className="text-sm text-zinc-500">Aucune commande pour l'instant. Réponds à des demandes du Marché pour décrocher tes premières missions.</p>
        ) : (
          <div className="space-y-2">
            {orders.slice(0, 5).map((o) => (
              <button key={o.id} onClick={() => navigate("/pro/commandes")} className="w-full flex items-center justify-between gap-3 text-sm border-b border-zinc-800/60 last:border-0 pb-2 text-left" data-testid={`pro-recent-order-${o.id}`}>
                <span className="text-zinc-300 truncate flex-1">{o.title || o.request_title || "Prestation"}</span>
                <StatusBadge status={o.status} />
                <span className="text-amber-400 font-mono shrink-0">{money(o.amount, o.currency)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
